import { useState, useCallback, useEffect, useRef } from 'react';
import { useWalletStore } from '@/store/walletStore';
import balanceService from '@/services/blockchain/balance.service';
import providerService from '@/services/blockchain/provider.service';
import storageService from '@/services/wallet/storage.service';
import balanceCacheService from '@/services/blockchain/balance-cache.service';
import { getPopularTokens } from '@/config/tokens';
import config from '@/config';
import { getCustomNetworks } from '@/config/networks';
import { Token } from '@/types';

/**
 * Redes incluidas en el balance agregado cross-network (allNetworksTokens),
 * usado por el Dashboard ("Mis Activos") y la página de Assets: OMDB/BSC
 * más cualquier red que el usuario haya agregado desde Settings > Redes.
 */
export function getAssetNetworkIds(): string[] {
  return ['omdb', 'bsc', ...getCustomNetworks().map((n) => n.id)];
}

/**
 * Hook de Balance unificado
 * Proporciona acceso a los balances y la capacidad de refrescarlos
 */
export function useBalance() {
  // Selectores de Zustand para evitar re-renders innecesarios
  const address = useWalletStore(state => state.address);
  const selectedNetwork = useWalletStore(state => state.selectedNetwork);
  const tokens = useWalletStore(state => state.tokens);
  const allNetworksTokens = useWalletStore(state => state.allNetworksTokens);
  const nativeBalance = useWalletStore(state => state.balance);
  const totalValueUSD = useWalletStore(state => state.totalBalanceUSD);
  const loading = useWalletStore(state => state.isLoading);
  const lastUpdate = useWalletStore(state => state.lastBalanceUpdate);

  // Acciones de Zustand
  const setTokens = useWalletStore(state => state.setTokens);
  const setAllNetworksTokens = useWalletStore(state => state.setAllNetworksTokens);
  const setBalance = useWalletStore(state => state.setBalance);
  const setTotalBalanceUSD = useWalletStore(state => state.setTotalBalanceUSD);
  const setIsLoading = useWalletStore(state => state.setIsLoading);
  const setLastBalanceUpdate = useWalletStore(state => state.setLastBalanceUpdate);

  const totalAllNetworksUSD = useWalletStore(state => state.totalAllNetworksUSD);
  const setTotalAllNetworksUSD = useWalletStore(state => state.setTotalAllNetworksUSD);
  const allNetworksLoaded = useWalletStore(state => state.allNetworksLoaded);
  const setAllNetworksLoaded = useWalletStore(state => state.setAllNetworksLoaded);

  const [error, setError] = useState<string | null>(null);

  const balanceGenRef = useRef(0);
  const allNetworksGenRef = useRef(0);

  const nativeSymbol = config.getNetwork(selectedNetwork)?.nativeCurrency?.symbol || 'ETH';

  /**
   * Al montar el componente, cargar el balance cacheado si existe
   */
  useEffect(() => {
    if (!address) return;
    const cachedBalance = balanceCacheService.getCachedGlobalBalance(address);
    if (cachedBalance !== null) {
      setTotalAllNetworksUSD(cachedBalance);
      setAllNetworksLoaded(true);
    }
  }, [address, setTotalAllNetworksUSD, setAllNetworksLoaded]);

  /**
   * Refrescar balances y precios de forma manual o en segundo plano
   */
  const refreshBalance = useCallback(async (showLoading = true) => {
    if (!address) return;

    balanceGenRef.current += 1;
    const gen = balanceGenRef.current;

    try {
      if (showLoading) setIsLoading(true);
      setError(null);

      const provider = providerService.getProvider(selectedNetwork);
      if (!provider) {
        throw new Error(`No se pudo conectar con la red ${selectedNetwork}`);
      }

      // 1. Obtener la lista base de tokens (nativo + populares + custom)
      const baseTokens = getNetworkTokens(selectedNetwork);

      // 2. Refrescar balances y precios en bloque
      const result = await balanceService.refreshFullAccountState(
        address,
        selectedNetwork,
        baseTokens,
        provider
      );

      // 3. Extraer el balance nativo para la variable dedicada del store
      const nativeToken = result.tokens.find(t => t.isNative || t.symbol === nativeSymbol);
      const newNativeBalance = nativeToken?.balance ? parseFloat(nativeToken.balance) : 0;

      // 4. Actualizar store global (esto notificará a todos los componentes)
      if (balanceGenRef.current !== gen) return; // red cambió mientras cargaba, descartar
      setTokens(result.tokens);
      setBalance(newNativeBalance);
      setTotalBalanceUSD(result.totalValueUSD);
      setLastBalanceUpdate(Date.now());

    } catch (err) {
      console.error('[useBalance] Error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al actualizar balances');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [
    address,
    selectedNetwork,
    nativeSymbol,
    setTokens,
    setBalance,
    setTotalBalanceUSD,
    setIsLoading,
    setLastBalanceUpdate
  ]);

  /**
   * Suma el balance USD de todas las redes en paralelo.
   * Usa caché para mostrar el último valor conocido mientras se recalcula en background.
   */
  const refreshAllNetworksBalance = useCallback(async () => {
    if (!address) return;

    allNetworksGenRef.current += 1;
    const gen = allNetworksGenRef.current;

    try {
      // 1. Intentar cargar del caché primero
      const cachedBalance = balanceCacheService.getCachedGlobalBalance(address);
      if (cachedBalance !== null) {
        setTotalAllNetworksUSD(cachedBalance);
        setAllNetworksLoaded(true);
      } else {
        // Si no hay caché, mostrar skeleton
        setAllNetworksLoaded(false);
      }

      // 2. Recalcular en background
      const networksData: Record<string, { tokens: Token[]; provider: import('ethers').Provider }> = {};

      for (const networkId of getAssetNetworkIds()) {
        const provider = providerService.getProvider(networkId);
        if (!provider) continue;
        networksData[networkId] = {
          tokens: getNetworkTokens(networkId),
          provider,
        };
      }

      const { totalAllNetworks, tokens: allTokens } = await balanceService.refreshAllNetworksBalance(address, networksData);

      // 3. Actualizar con el nuevo valor y guardar en caché
      if (allNetworksGenRef.current !== gen) return; // resultado stale, descartar
      setTotalAllNetworksUSD(totalAllNetworks);
      setAllNetworksTokens(allTokens);
      balanceCacheService.setCachedGlobalBalance(address, totalAllNetworks);
      setAllNetworksLoaded(true);
    } catch (err) {
      console.error('[useBalance] Error en balance cross-network:', err);
      // Si falla, mantener el caché o skeleton visible
      setAllNetworksLoaded(true);
    }
  }, [address, setAllNetworksLoaded, setTotalAllNetworksUSD, setAllNetworksTokens]);

  return {
    // Estado
    nativeBalance,
    nativeSymbol,
    tokens,
    allNetworksTokens,
    totalValueUSD,
    totalAllNetworksUSD,
    allNetworksLoaded,
    loading,
    error,
    lastUpdate,

    // Acciones
    refreshBalance,
    refreshAllNetworksBalance,
  };
}

/**
 * Obtiene los tokens configurados para una red específica
 */
export function getNetworkTokens(networkId: string): Token[] {
  const network = config.getNetwork(networkId);

  // Token nativo de la red
  const nativeToken: Token = {
    symbol: network?.nativeCurrency?.symbol || 'ETH',
    name: network?.nativeCurrency?.name || 'Ethereum',
    address: '0x0000000000000000000000000000000000000000',
    decimals: network?.nativeCurrency?.decimals || 18,
    isNative: true,
    logoUrl: network?.nativeCurrencyLogoUrl || '',
    network: networkId
  };

  // 1. Tokens populares de la red (respetando visibilidad configurada por el usuario)
  const popularTokens = getPopularTokens(networkId)
    .filter(t => storageService.isTokenVisible(t.address, networkId));

  // 2. Tokens personalizados del usuario
  const customTokens = storageService.getCustomTokens()
    .filter(t => t.network === networkId)
    .filter(t => storageService.isTokenVisible(t.address, networkId));

  // 3. Unificar evitando duplicados por dirección o símbolo
  const allTokens: Token[] = [];
  const seenAddresses = new Set([nativeToken.address.toLowerCase()]);
  const seenSymbols = new Set([nativeToken.symbol.toLowerCase()]);

  if (storageService.isTokenVisible(nativeToken.address, networkId)) {
    allTokens.push(nativeToken);
  }

  [...popularTokens, ...customTokens].forEach(token => {
    const addr = (token.address || '').toLowerCase();
    const sym = (token.symbol || '').toLowerCase();

    if (addr && !seenAddresses.has(addr) && !seenSymbols.has(sym)) {
      seenAddresses.add(addr);
      seenSymbols.add(sym);
      allTokens.push({
        ...token,
        balance: '0',
        balanceFormatted: '0',
        network: networkId
      });
    }
  });

  return allTokens;
}

export default useBalance;
